import React, { useRef, useEffect, useState, memo } from 'react';
import { createPortal } from 'react-dom';

/**
 * ResponsiveFrame - рендерит контент в iframe с реальным viewport
 */
const ResponsiveFrame = memo(({ children, width = '100%', height = '100%' }) => {
  const iframeRef = useRef(null);
  const [mountNode, setMountNode] = useState(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const initIframe = () => {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!doc) return;

      // Если уже инициализирован, просто берём root
      let root = doc.getElementById('root');
      if (root) {
        setMountNode(root);
        setIsReady(true);
        return;
      }

      // Инициализируем iframe
      doc.open();
      doc.write(`<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<style>
*, *::before, *::after { box-sizing: border-box; }
html, body { margin: 0; padding: 0; font-family: system-ui, -apple-system, sans-serif; background: #fff; color: #0f172a; line-height: 1.5; }
img { max-width: 100%; height: auto; }
</style>
</head>
<body><div id="root"></div></body>
</html>`);
      doc.close();

      // Копируем стили
      document.querySelectorAll('style, link[rel="stylesheet"]').forEach(el => {
        try { doc.head.appendChild(el.cloneNode(true)); } catch (e) {}
      });

      root = doc.getElementById('root');
      setMountNode(root);
      // Небольшая задержка для применения стилей
      setTimeout(() => setIsReady(true), 50);
    };

    // Инициализируем после монтирования
    const timer = setTimeout(initIframe, 0);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div style={{ width, height, position: 'relative' }}>
      <iframe
        ref={iframeRef}
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
          display: 'block',
          background: '#fff',
          opacity: isReady ? 1 : 0,
          transition: 'opacity 0.15s ease-in-out'
        }}
        title="Preview"
      />
      {!isReady && (
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f8fafc'
        }}>
          <div style={{
            width: 24,
            height: 24,
            border: '3px solid #e2e8f0',
            borderTopColor: '#3b82f6',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite'
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}
      {mountNode && createPortal(children, mountNode)}
    </div>
  );
});

ResponsiveFrame.displayName = 'ResponsiveFrame';

export default ResponsiveFrame;
